import type { AppLanguage } from "locales";

/**
 * Landing page content catalogue.
 *
 * Plain JSX/SVG stays in index.tsx; only user-visible text lives here so that
 * we can swap whole copy blocks per language without touching markup. SVG mockup
 * names (avatar initials, demo person names, demo filenames) are also localised
 * so the screenshot tells a believable story in both languages.
 */
export interface LandingContent {
  meta: { title: string; description: string };
  download: {
    label: string;
    macArm64Title: string;
    macArm64Hint: string;
    macIntelTitle: string;
    macIntelHint: string;
  };
  hero: {
    badge: string;
    titleBefore: string;
    titleHighlight: string;
    titleAfter: string;
    subtitle: string;
    getStarted: string;
    signIn: string;
  };
  heroMockup: {
    appName: string;
    user1Initials: string;
    user2Initials: string;
    user3Initials: string;
    incomingFrom: string;
    incomingText: string;
    outgoingText: string;
    fileAttachmentName: string;
    callActive: string;
    callParticipants: string;
    join: string;
    typeMessage: string;
  };
  heroFeatures: { title: string; desc: string }[];
  heroFeaturesIntro: { title: string; subtitle: string };

  messagingSection: {
    title: string;
    items: string[];
    mockup: {
      incoming: string;
      outgoing: string;
      edited: string;
      editedLabel: string;
      mention: string;
      mentionTag: string;
      today: string;
    };
  };

  videoSection: {
    title: string;
    items: { text: string; strongPrefix?: string }[];
    mockup: {
      user1Initials: string;
      user1Name: string;
      user2Initials: string;
      user2Name: string;
      user3Initials: string;
      user3Name: string;
      user4Initials: string;
      user4Name: string;
    };
  };

  newFeatures: {
    title: string;
    badge: string;
    subtitle: string;
    seeFull: string;
    cards: { title: string; desc: string }[];
  };

  allFeatures: {
    title: string;
    subtitle: string;
    seeDetails: string;
    items: { title: string; desc: string }[];
  };

  why: {
    title: string;
    subtitle: string;
    highlights: { label: string; value: string }[];
  };

  cta: {
    title: string;
    subtitle: string;
    getStarted: string;
    contactSales: string;
  };

  exploreAll: string;
}

const en: LandingContent = {
  meta: {
    title: "SMeet — AI Messenger with Video Calls & Meeting Intelligence",
    description:
      "Secure corporate messenger with HD video calls, AI meeting transcripts, summaries, voice translation in 11 languages, and chat. Replace Slack + Zoom on your own servers.",
  },
  download: {
    label: "Download for Mac",
    macArm64Title: "Mac (Apple Silicon)",
    macArm64Hint: "M1 / M2 / M3",
    macIntelTitle: "Mac (Intel)",
    macIntelHint: "Coming soon",
  },
  hero: {
    badge: "Enterprise Communication Platform",
    titleBefore: "One app for messaging, ",
    titleHighlight: "video calls",
    titleAfter: ", and team collaboration",
    subtitle:
      "SMeet replaces your scattered toolset with a single, secure corporate messenger. Group chats, HD conferencing, file sharing, and real-time collaboration — all in one place.",
    getStarted: "Get Started Free",
    signIn: "Sign In",
  },
  heroMockup: {
    appName: "SMeet",
    user1Initials: "DP",
    user2Initials: "TS",
    user3Initials: "MK",
    incomingFrom: "David Park",
    incomingText: "Hey team, the Q4 report is ready!",
    outgoingText: "Great, I’ll review it right away",
    fileAttachmentName: "Q4-Report-Final.pdf",
    callActive: "Group call active",
    callParticipants: "3 participants · 12:34",
    join: "Join",
    typeMessage: "Type a message...",
  },
  heroFeatures: [
    {
      title: "Instant Messaging",
      desc: "1-on-1 and group chats with reactions, mentions, read receipts, and full edit history",
    },
    {
      title: "Video Conferencing",
      desc: "HD group video calls with screen sharing and recording — built right into every chat",
    },
    {
      title: "Team Workspaces",
      desc: "Public and private groups with member management, roles, and a discoverable catalog",
    },
    {
      title: "AI Meeting Intelligence",
      desc: "Automatic summaries, action items, and 🧠 search across every recorded meeting — runs on your own servers, no per-seat AI fees",
    },
  ],
  heroFeaturesIntro: {
    title: "Everything your team needs",
    subtitle: "The core pillars of enterprise communication, unified in a single platform.",
  },
  messagingSection: {
    title: "Messaging that keeps up with your team",
    items: [
      "Real-time delivery with read receipts (✓ sent, ✓✓ read)",
      "GIF picker with millions of GIFs, trending, and masonry preview grid",
      "Video messages — round video circles with inline playback",
      "Voice messages with waveform, speed control (1x/1.5x/2x), AI transcription, and voice-to-text dictation",
      "React with emoji — 6 presets + unlimited custom corporate emoji",
      "@mention teammates with autocomplete and notification badges",
      "Swipe-to-reply gesture on mobile with haptic feedback",
      "Reply, forward, pin, edit, and delete — full message actions",
      "Media gallery panel: Photos, Videos, Files, Links, Voice — all organized",
      "Saved Messages — personal space for notes, bookmarks, and drafts",
      "Chat folders — organize chats by project, team, or workflow",
      "Dark & light themes with system preference detection",
      "Sub-threads for focused discussions in groups",
      "Global search across all chats — find any message instantly",
      "Drag & drop or paste images and files up to 2 GB",
      "Smooth scrolling through long conversations — even with thousands of messages",
    ],
    mockup: {
      incoming: "Can we ship this by Friday?",
      outgoing: "Yes! Already pushed the fix",
      edited: "Updated the docs, check the link",
      editedLabel: "edited",
      mention: "please review the PR",
      mentionTag: "@sarah",
      today: "Today",
    },
  },
  videoSection: {
    title: "Video conferencing built right in",
    items: [
      { text: "Start group video calls from any chat with one click" },
      { text: "Voice-only calls or full video — choose when accepting" },
      { text: "Built-in incoming call screen with custom ringtone" },
      { text: "Automatic reconnection on network drops with on-screen status" },
      { text: "Virtual backgrounds — blur, gradients, or custom images" },
      { text: "Picture-in-Picture: keep video visible while multitasking" },
      { text: "Network quality indicator (1-5 bars) per participant" },
      { text: "Screen sharing for presentations and code reviews" },
      { text: "Cloud recording with inline chat previews and one-click playback" },
      { text: "Works seamlessly across Chrome, Safari, and Firefox — no plugins" },
      { text: "Active call indicators so no one misses a meeting" },
      {
        strongPrefix: "AutoRecord Video",
        text: " — group owners can have every conference recorded automatically",
      },
      {
        strongPrefix: "Per-speaker AI transcription",
        text: " — every recorded call labelled by speaker, timestamped, downloadable",
      },
      {
        strongPrefix: "AI Meeting Coach",
        text: " — auto summary, decisions, action items, and speaking-time chart after every call",
      },
      {
        strongPrefix: "Meeting Memory",
        text: " — search and ask questions across every recording with deep-links to the exact second",
      },
    ],
    mockup: {
      user1Initials: "DP",
      user1Name: "David Park",
      user2Initials: "SK",
      user2Name: "Sarah Kim",
      user3Initials: "MR",
      user3Name: "Mike Ross",
      user4Initials: "AJ",
      user4Name: "Anna Jones",
    },
  },
  newFeatures: {
    title: "Latest additions to SMeet",
    badge: "New",
    subtitle:
      "Telegram-level messaging, smart AI features, and native desktop app — just shipped.",
    seeFull: "See full details →",
    cards: [
      {
        title: "GIF & Sticker Picker",
        desc: "Search and send GIFs from a library of millions, with trending suggestions and a smooth preview grid. Custom sticker packs with recent history.",
      },
      {
        title: "Video Messages",
        desc: "Record and send round video messages directly from chat. Compact circular player with inline playback.",
      },
      {
        title: "AI Voice Transcription",
        desc: "One-click transcription of any voice message. Automatic language detection across 99 languages — runs entirely on your own servers.",
      },
      {
        title: "Voice-to-Text Dictation",
        desc: "Switch to text-only mode while recording — your voice is transcribed and sent as a plain text message. Speak instead of typing.",
      },
      {
        title: "Per-Speaker Conference Transcription 🆕",
        desc: "Every group call recording is automatically transcribed with each speaker labelled by name out of the box. Live status while it runs; download as text or subtitle file when ready. Audio never leaves your infrastructure.",
      },
      {
        title: "AI Meeting Coach 🆕",
        desc: "After every recorded meeting you get an executive summary, decisions, action items with assignees, and a speaking-time chart per participant — automatically, no clicks needed. Runs on your own servers.",
      },
      {
        title: "Meeting Memory 🆕",
        desc: "Ask “When did we decide on the Q3 budget?” in plain language — get a grounded answer with sources, and one-click jump to the exact second of the right recording. Respects each user’s access rights.",
      },
      {
        title: "Voice Clone Translation 🆕",
        desc: "Enroll your voice once with a 30-second clip. From then on, every voice message you send can be played back in 11 languages — translated into the recipient’s preferred language while keeping your own voice. Stays entirely on your servers.",
      },
      {
        title: "Media Gallery",
        desc: "Browse all shared media in one panel — Photos, Videos, Files, Links, and Voice tabs. Grid view for images, list view for documents.",
      },
      {
        title: "Dark & Light Themes",
        desc: "Toggle between dark and light themes. Respects system preference, saves your choice. All UI elements adapt seamlessly.",
      },
      {
        title: "Desktop App",
        desc: "Native macOS app with system tray, notifications, session sync with the browser, and automatic updates.",
      },
      {
        title: "Saved Messages",
        desc: "Personal space for notes and bookmarks. Forward any message to Saved Messages, write to yourself, attach files — always at the top of your chat list.",
      },
      {
        title: "Swipe Gestures",
        desc: "Swipe right on any message to reply — just like Telegram. Haptic feedback on mobile, smooth spring animations, and pull-to-refresh for the chat list.",
      },
      {
        title: "Message Threads",
        desc: "Slack-style sub-threads in group chats. Reply in a dedicated side panel without cluttering the main conversation. Reply count badges on parent messages.",
      },
    ],
  },
  allFeatures: {
    title: "More built-in capabilities",
    subtitle: "Every feature your organization needs, without third-party add-ons.",
    seeDetails: "See detailed breakdowns →",
    items: [
      {
        title: "Per-Speaker Conference Transcription",
        desc: "Every recorded meeting is automatically transcribed with each participant labelled by name. Searchable, timestamped, and downloadable as text or subtitle file — the foundation the Coach and Memory features build on.",
      },
      {
        title: "AI Meeting Coach",
        desc: "After every recording, you get an executive summary, decisions, action items with assignees, topic tags, and a speaking-time chart per participant. Runs on your own servers — no per-seat AI fees.",
      },
      {
        title: "Meeting Memory",
        desc: "Ask “When did we decide on the Q3 budget?” in plain language. Search and AI Q&A across every transcript and message you can access — every answer deep-links to the exact second of the right recording.",
      },
      {
        title: "Voice Clone Translation",
        desc: "Record a 30-second voice sample once. From then on, anyone can play back your voice messages in 11 languages — in your own voice. Stays entirely on your servers.",
      },
      {
        title: "Native iOS Incoming Calls",
        desc: "Telegram-/WhatsApp-style incoming calls. Ring your iPhone even when the app is closed or the screen is locked — with the native call screen and system ringtone.",
      },
      {
        title: "Custom Corporate Emoji",
        desc: "Upload branded emoji for your team. Admins manage the library; everyone uses them in any chat.",
      },
      {
        title: "File Sharing",
        desc: "Drag & drop files up to 2 GB. Inline image previews, lightbox viewer, and secure cloud storage.",
      },
      {
        title: "Push Notifications",
        desc: "Real-time push on iOS, Android, web, and desktop — your team stays in sync wherever they are.",
      },
      {
        title: "Live Streaming",
        desc: "Broadcast to your organization with real-time chat, viewer tracking, and moderation tools.",
      },
      {
        title: "Voice Messages",
        desc: "Record and send audio with waveform player, speed control (1x/1.5x/2x), seek, duration, AI transcription, and voice-to-text dictation.",
      },
      {
        title: "Global Search",
        desc: "Search across all chats at once — find any message, in any conversation. Toggle between chat filter and message search.",
      },
      {
        title: "Reply & Forward",
        desc: "Reply to specific messages with a quoted preview. Forward to any chat with one click and a 'Forwarded from' label.",
      },
      {
        title: "Pin & Typing",
        desc: "Pin key messages in group chats with an expandable panel. See real-time typing indicators in every conversation.",
      },
      {
        title: "Chat Folders",
        desc: "Organize chats into custom folders. Filter by folder tabs, add chats via right-click menu. Cross-device sync.",
      },
      {
        title: "Multi-Language",
        desc: "Built-in support for English and Russian out of the box. Easily extensible to any language your team needs.",
      },
      {
        title: "Enterprise Security",
        desc: "Token-based authentication, QR-code login, sign-in with Google / Facebook / Twitter, and role-based access.",
      },
      {
        title: "QR Code Login",
        desc: "Scan a QR code from your phone to instantly authenticate on any browser — no password needed.",
      },
    ],
  },
  why: {
    title: "Built for the way enterprises work",
    subtitle:
      "Privacy-first by design, fast on the right hardware, and ready to run inside your own infrastructure.",
    highlights: [
      { label: "Privacy", value: "Your data stays on your servers" },
      { label: "AI", value: "No per-seat fees · runs locally" },
      { label: "Scale", value: "Built for hundreds of concurrent users" },
      { label: "Real-Time", value: "Sub-second message delivery" },
      { label: "Calls", value: "HD group video, recorded & transcribed" },
      { label: "Storage", value: "Files & recordings in your cloud" },
      { label: "Platforms", value: "Web · macOS · iOS · Android" },
      { label: "Deploy", value: "Run on your own infrastructure" },
    ],
  },
  cta: {
    title: "Ready to unify your team communication?",
    subtitle: "Replace Slack, Zoom, and internal payment tools with a single secure platform.",
    getStarted: "Get Started Free",
    contactSales: "Contact Sales",
  },
  exploreAll: "Explore all features in detail →",
};

const ru: LandingContent = {
  meta: {
    title: "SMeet — ИИ-мессенджер с видеозвонками и аналитикой встреч",
    description:
      "Безопасный корпоративный мессенджер: HD-видеозвонки, ИИ-расшифровка встреч, краткое содержание, голосовой перевод на 11 языков и чат. Полноценная замена Slack и Zoom.",
  },
  download: {
    label: "Скачать для Mac",
    macArm64Title: "Mac (Apple Silicon)",
    macArm64Hint: "M1 / M2 / M3",
    macIntelTitle: "Mac (Intel)",
    macIntelHint: "Скоро",
  },
  hero: {
    badge: "Корпоративная коммуникационная платформа",
    titleBefore: "Одно приложение для переписки, ",
    titleHighlight: "видеозвонков",
    titleAfter: " и совместной работы",
    subtitle:
      "SMeet заменяет россыпь инструментов одним безопасным корпоративным мессенджером. Групповые чаты, HD-конференции, обмен файлами и совместная работа в реальном времени — всё в одном месте.",
    getStarted: "Начать бесплатно",
    signIn: "Войти",
  },
  heroMockup: {
    appName: "SMeet",
    user1Initials: "ДП",
    user2Initials: "ТС",
    user3Initials: "МК",
    incomingFrom: "Дмитрий Петров",
    incomingText: "Команда, отчёт за Q4 готов!",
    outgoingText: "Отлично, сейчас посмотрю",
    fileAttachmentName: "Отчёт-Q4-final.pdf",
    callActive: "Идёт групповой звонок",
    callParticipants: "3 участника · 12:34",
    join: "Войти",
    typeMessage: "Напишите сообщение...",
  },
  heroFeatures: [
    {
      title: "Мгновенный обмен сообщениями",
      desc: "Личные и групповые чаты с реакциями, упоминаниями, отметками о прочтении и историей правок",
    },
    {
      title: "Видеоконференции",
      desc: "HD-видеозвонки с демонстрацией экрана и записью — встроены в каждый чат",
    },
    {
      title: "Командные пространства",
      desc: "Публичные и приватные группы — с ролями, управлением участниками и общим каталогом, где легко найти нужную команду",
    },
    {
      title: "ИИ-аналитика встреч",
      desc: "Краткое содержание, задачи и 🧠 умный поиск по каждой записанной встрече — формируется автоматически",
    },
  ],
  heroFeaturesIntro: {
    title: "Всё, что нужно вашей команде",
    subtitle: "Главные составляющие корпоративного общения — в одной платформе.",
  },
  messagingSection: {
    title: "Мессенджер, который успевает за вашей командой",
    items: [
      "Доставка в реальном времени с отметками о прочтении (✓ отправлено, ✓✓ прочитано)",
      "Выбор GIF: миллионы анимаций, тренды и удобная сетка предпросмотра",
      "Видеосообщения — круглые видеокружки с проигрыванием прямо в чате",
      "Голосовые сообщения со звуковой дорожкой, регулировкой скорости (1x/1.5x/2x), ИИ-расшифровкой и набором голосом",
      "Реакции эмодзи — 6 готовых и неограниченное число своих корпоративных эмодзи",
      "Упоминания коллег через @ — с подсказками при вводе и уведомлениями",
      "Свайп для ответа на мобильном с виброоткликом",
      "Ответ, пересылка, закрепление, редактирование и удаление — полный набор действий с сообщением",
      "Панель медиа: Фото, Видео, Файлы, Ссылки, Голосовые — всё разложено по полочкам",
      "«Сохранённые сообщения» — личное пространство для заметок, закладок и черновиков",
      "Папки чатов — сортируйте чаты по проектам, командам или рабочим процессам",
      "Тёмная и светлая темы с автоопределением системных настроек",
      "Подветки сообщений для сфокусированных обсуждений в группах",
      "Глобальный поиск по всем чатам — найдите любое сообщение мгновенно",
      "Перетаскивание или вставка картинок и файлов до 2 ГБ",
      "Плавная прокрутка длинной переписки — даже на тысячах сообщений",
    ],
    mockup: {
      incoming: "Успеем ли отгрузить к пятнице?",
      outgoing: "Да! Уже выкатил фикс",
      edited: "Обновил доку, посмотри ссылку",
      editedLabel: "изменено",
      mention: "посмотри, пожалуйста, PR",
      mentionTag: "@саша",
      today: "Сегодня",
    },
  },
  videoSection: {
    title: "Видеоконференции встроены прямо в чат",
    items: [
      { text: "Запуск группового видеозвонка из любого чата одним кликом" },
      { text: "Только голос или с видео — выбирайте при ответе" },
      { text: "Встроенный экран входящего вызова с собственным рингтоном" },
      { text: "Автоматическое переподключение при обрыве связи с индикатором статуса" },
      { text: "Виртуальные фоны — размытие, градиенты или своя картинка" },
      { text: "«Картинка в картинке»: видео остаётся на экране, пока вы заняты другими делами" },
      { text: "Индикатор качества сети (1–5 делений) для каждого участника" },
      { text: "Демонстрация экрана для презентаций и код-ревью" },
      { text: "Запись в облако с превью в чате и проигрыванием в один клик" },
      { text: "Работает без плагинов в Chrome, Safari и Firefox" },
      { text: "Индикаторы активного звонка — никто не пропустит встречу" },
      {
        strongPrefix: "Автозапись видео",
        text: " — владельцы группы могут включить автоматическую запись каждой конференции",
      },
      {
        strongPrefix: "ИИ-расшифровка по дикторам",
        text: " — каждая запись звонка разложена по говорящим, с таймкодами и возможностью скачать",
      },
      {
        strongPrefix: "ИИ-коуч встреч",
        text: " — краткое содержание, решения, задачи и диаграмма времени речи после каждого звонка",
      },
      {
        strongPrefix: "Память встреч",
        text: " — поиск и вопросы на естественном языке по всем записям, с переходом на нужную секунду",
      },
    ],
    mockup: {
      user1Initials: "ДП",
      user1Name: "Дмитрий Петров",
      user2Initials: "СК",
      user2Name: "Светлана Котова",
      user3Initials: "МР",
      user3Name: "Михаил Романов",
      user4Initials: "АИ",
      user4Name: "Анна Иванова",
    },
  },
  newFeatures: {
    title: "Свежие возможности SMeet",
    badge: "Новое",
    subtitle:
      "Мессенджер уровня Telegram, умные ИИ-функции и приложение для компьютера — уже доступно.",
    seeFull: "Подробнее →",
    cards: [
      {
        title: "Пикер GIF и стикеров",
        desc: "Поиск и отправка GIF из библиотеки на миллионы файлов, тренды и плавная сетка предпросмотра. Свои наборы стикеров и история недавних.",
      },
      {
        title: "Видеосообщения",
        desc: "Запись и отправка круглых видеосообщений прямо из чата. Компактный круглый проигрыватель с воспроизведением прямо в чате.",
      },
      {
        title: "ИИ-расшифровка голосовых",
        desc: "Расшифровка любого голосового сообщения в один клик. Автоопределение языка из 99 — без ручного выбора.",
      },
      {
        title: "Голос в текст",
        desc: "Переключитесь в текстовый режим во время записи — голос распознается и отправится обычным сообщением. Говорите вместо того, чтобы печатать.",
      },
      {
        title: "Расшифровка конференций по дикторам 🆕",
        desc: "Каждая запись группового звонка автоматически расшифровывается с указанием, кто что сказал. Видно статус в реальном времени, готовый текст можно скачать как документ или субтитры.",
      },
      {
        title: "ИИ-коуч встреч 🆕",
        desc: "После каждой записанной встречи — краткое содержание, принятые решения, задачи с ответственными и диаграмма, кто сколько говорил. Всё формируется автоматически, без единого клика.",
      },
      {
        title: "Память встреч 🆕",
        desc: "Спрашивайте «Когда мы согласовали бюджет на Q3?» обычным языком — ответ со ссылками на источники и переходом на нужную секунду нужной записи. Учитывает права доступа каждого пользователя.",
      },
      {
        title: "Голосовой клон-перевод 🆕",
        desc: "Запишите 30-секундный образец голоса один раз. После этого ваши голосовые могут проигрываться на 11 языках — в переводе на язык получателя и при этом вашим голосом.",
      },
      {
        title: "Медиагалерея",
        desc: "Все вложения в одной панели — вкладки Фото, Видео, Файлы, Ссылки, Голосовые. Сетка для изображений, список для документов.",
      },
      {
        title: "Тёмная и светлая темы",
        desc: "Переключайтесь между тёмной и светлой темами. Учитывает системные настройки, запоминает выбор. Все элементы адаптируются.",
      },
      {
        title: "Десктоп-приложение",
        desc: "Нативное приложение для macOS с системным треем, уведомлениями, синхронизацией сессии с браузером и автообновлениями.",
      },
      {
        title: "Сохранённые сообщения",
        desc: "Личное пространство для заметок и закладок. Пересылайте любое сообщение себе, пишите заметки, прикрепляйте файлы — всегда в верхней части списка чатов.",
      },
      {
        title: "Свайп-жесты",
        desc: "Свайп вправо для ответа — как в Telegram. Виброотклик на телефоне, плавные анимации и обновление списка чатов жестом «потяните вниз».",
      },
      {
        title: "Подветки сообщений",
        desc: "Подветки в стиле Slack в групповых чатах. Отвечайте в отдельной боковой панели, не засоряя основную переписку. На исходном сообщении видно число ответов.",
      },
    ],
  },
  allFeatures: {
    title: "Ещё больше встроенных возможностей",
    subtitle: "Всё, что нужно вашей организации, без сторонних дополнений.",
    seeDetails: "Подробнее о каждой функции →",
    items: [
      {
        title: "Расшифровка конференций по дикторам",
        desc: "Каждая запись встречи автоматически расшифровывается с подписями говорящих. Поиск, таймкоды, скачивание в текст или субтитры — на ней строятся «ИИ-коуч» и «Память встреч».",
      },
      {
        title: "ИИ-коуч встреч",
        desc: "После каждой записи — краткое содержание, решения, задачи с ответственными, теги тем и диаграмма времени речи по участникам.",
      },
      {
        title: "Память встреч",
        desc: "Спрашивайте «Когда мы согласовали бюджет на Q3?» обычным языком. Поиск и ответы на вопросы по всем доступным расшифровкам и сообщениям — каждый ответ ссылается на конкретную секунду нужной записи.",
      },
      {
        title: "Голосовой клон-перевод",
        desc: "Запишите 30-секундный образец голоса один раз. После этого любой сможет проиграть ваши голосовые на 11 языках — вашим же голосом.",
      },
      {
        title: "Нативные входящие звонки на iOS",
        desc: "Входящие в стиле Telegram/WhatsApp. iPhone звонит, даже когда приложение закрыто или экран заблокирован — с системным экраном вызова и рингтоном.",
      },
      {
        title: "Корпоративные эмодзи",
        desc: "Загрузите брендовые эмодзи для команды. Администраторы управляют библиотекой; все используют их в любых чатах.",
      },
      {
        title: "Обмен файлами",
        desc: "Перетаскивание файлов до 2 ГБ. Превью картинок прямо в чате, полноэкранный просмотр и защищённое облачное хранилище.",
      },
      {
        title: "Push-уведомления",
        desc: "Мгновенные push-уведомления на iOS, Android, в вебе и на компьютере — команда всегда в курсе.",
      },
      {
        title: "Прямые эфиры",
        desc: "Транслируйте на всю организацию с чатом в реальном времени, метриками зрителей и инструментами модерации.",
      },
      {
        title: "Голосовые сообщения",
        desc: "Запись и отправка аудио со звуковой дорожкой, регулировкой скорости (1x/1.5x/2x), перемоткой, длительностью, ИИ-расшифровкой и набором голосом.",
      },
      {
        title: "Глобальный поиск",
        desc: "Поиск сразу по всем чатам — найдите любое сообщение в любой переписке. Переключение между фильтром чатов и поиском по сообщениям.",
      },
      {
        title: "Ответ и пересылка",
        desc: "Отвечайте на конкретные сообщения с цитированием. Пересылка в любой чат одним кликом с пометкой «Переслано от».",
      },
      {
        title: "Закрепление и набор",
        desc: "Закрепляйте важные сообщения в групповых чатах с раскрываемой панелью. Индикаторы набора в реальном времени в каждой переписке.",
      },
      {
        title: "Папки чатов",
        desc: "Раскладывайте чаты по своим папкам. Фильтр-вкладки, добавление по правому клику, синхронизация между устройствами.",
      },
      {
        title: "Мультиязычность",
        desc: "Встроенная поддержка русского и английского. Легко расширяется на любой нужный язык.",
      },
      {
        title: "Корпоративная безопасность",
        desc: "Токенная аутентификация, вход по QR-коду, через Google / Facebook / Twitter и ролевая модель доступа.",
      },
      {
        title: "Вход по QR-коду",
        desc: "Отсканируйте QR-код с телефона — и вы вошли в любом браузере, без пароля.",
      },
    ],
  },
  why: {
    title: "Создано под реальную работу команд",
    subtitle:
      "Всё для общения, звонков и совместной работы — в одном быстром и надёжном приложении.",
    highlights: [
      { label: "Реальное время", value: "Доставка сообщений быстрее секунды" },
      { label: "Звонки", value: "Групповое HD-видео с записью и расшифровкой" },
      { label: "Масштаб", value: "Рассчитан на сотни одновременных пользователей" },
      { label: "Платформы", value: "Web · macOS · iOS · Android" },
      { label: "Перевод", value: "Голосовые на 11 языках вашим же голосом" },
      { label: "Память встреч", value: "Поиск и ответы по всем записям" },
      { label: "Поиск", value: "Любое сообщение во всех чатах мгновенно" },
      { label: "Мобильные звонки", value: "Нативные входящие на iOS и Android" },
    ],
  },
  cta: {
    title: "Готовы объединить коммуникации команды?",
    subtitle: "Замените Slack и Zoom одной защищённой платформой для всей команды.",
    getStarted: "Начать бесплатно",
    contactSales: "Связаться с отделом продаж",
  },
  exploreAll: "Смотреть все возможности подробно →",
};

const catalogue: Record<AppLanguage, LandingContent> = { ru, en };

export function getLandingContent(lang: AppLanguage): LandingContent {
  return catalogue[lang] ?? catalogue.en;
}
